// Libraries
import { useRef, useEffect } from 'react';
import Head from 'next/head';
import { generateNextSeo } from 'next-seo/pages';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
/* Seo */
import { LocationPageSkuSeo } from '@scenes/LocationPage/LocationPage.SEO';
/* Stores */
import { useNavigationStoreActions } from '@store/client/store.navigation';
/* Constants & queries */
import useCartMutationCanaddservice from '@store/queries/cart/useCartMutation.canaddservice';
/* Utils */
import { getCityStateLocationData, getSkyLineImage } from '@scenes/LocationPage/locationPage.utils';
import { generateDynamicContentData, generateDynamicMetaData } from '@integrations/contentful/contentful.location.utils';
/* Types */
import { ContentBlockRendered, LocationPageProps, MetaData, LocationSkuTemplateUnion } from './locationPage.types';
import { IHash } from 'types/base.types';
/* Components */
import LazyComponent from '@components/LazyComponent';
import LocationGeoList from '@scenes/LocationPage/Parts/LocationGeoList';
import LocationHeaderImage from './Parts/LocationHeaderImage';
import SubHeader from './Parts/SubHeader';
/* Constants */
import { GUARANTEE_TEXTS } from './locationPage.constants';

/* Dynamic Components */
const ContentBlock = dynamic(() => import('./Parts/ContentBlock'));
const ReviewsBlock = dynamic(() => import('./Parts/ReviewsBlock'));
const TechInfoBlock = dynamic(() => import('./Parts/TechInfoBlock'));
const PlanGuaranteeBlock = dynamic(() => import('../PlansPage/Parts/PlanGuaranteeBlock'));
const TextContentBlock = dynamic(() => import('./Parts/TextContentBlock'));
const BlogPosts = dynamic(() => import('./Parts/BlogPosts'));

const LocationSkuPage = (props: LocationPageProps & { skuData: LocationSkuTemplateUnion }) => {
  const {
    locationData,
    skuData,
    ratingAttributes,
    reviews: { reviews },
    geoList,
    geoCoordinates,
  } = props;
  /* Store */
  const { setNavigationMenuPathing } = useNavigationStoreActions();

  /* hooks */
  const router = useRouter();
  const firstSixReviews = reviews?.slice(0, 6);
  const reviewsRef = useRef<HTMLDivElement | null>(null);
  /* mutate: call later */
  const { mutate: fetchCanAddService } = useCartMutationCanaddservice({
    skuId: skuData.id,
  });

  /* Contentful Specific Methods & Constants */
  const { city = '', state = '' } = getCityStateLocationData(locationData);
  const service = skuData.name;

  const mapper: IHash<string> = {
    city,
    state,
    service,
  };

  /* Location pages need special pathing for sku pages. So, lets update the zustand state */
  useEffect(() => {
    setNavigationMenuPathing(locationData.url!);

    return () => setNavigationMenuPathing();
  }, [locationData.url, setNavigationMenuPathing]);

  /* Get our data with substitutions and dynamic input data from Contentful */
  const contentBlocksData = generateDynamicContentData({ mapper, seedData: skuData.contentful.contentBlocks }) as ContentBlockRendered;
  const metaData = generateDynamicMetaData({ mapper, seedData: skuData.contentful.metaData! }) as MetaData;

  return (
    <>
      <Head>
        {generateNextSeo({
          canonical: `${process.env.NEXT_PUBLIC_HOST_URL_CANONICAL}${router.asPath}`,
          title: metaData.title!,
          description: metaData.description!,
        })}
      </Head>
      <LocationPageSkuSeo locationData={locationData} skuData={skuData} ratingAttributes={ratingAttributes} geoCoordinates={geoCoordinates} />
      <div dangerouslySetInnerHTML={{ __html: `<!-- ${props.cacheDate} -->` }} />
      <LocationHeaderImage imageSource={getSkyLineImage(locationData)} location={city || state} />
      <SubHeader
        skuId={skuData.id}
        onGetPriceClick={fetchCanAddService}
        onRatingClick={null}
        serviceName={service}
        cityName={city || state}
        serviceBullets={skuData.skuBullets}
        ratingAttributes={ratingAttributes!}
      />
      <ContentBlock
        serviceImage="/_nextassets_/images/peace_of_mind.jpg"
        title={contentBlocksData.contentOneTitle}
        cityDescriptions={contentBlocksData.contentOneBody}
      />
      <LazyComponent minHeight="300px">
        <TechInfoBlock locationOrCategory={service} />
      </LazyComponent>
      <LazyComponent minHeight={reviews ? '300px' : ''}>
        {reviews && <ReviewsBlock reviews={firstSixReviews} cityName={city} reviewsRef={reviewsRef} />}
      </LazyComponent>
      <PlanGuaranteeBlock title={GUARANTEE_TEXTS.title} text={GUARANTEE_TEXTS.subTitle} classes="paddingTop-huge" />
      <TextContentBlock title={contentBlocksData.contentTwoTitle} serviceDescriptions={contentBlocksData.contentTwoBody} />
      <LazyComponent minHeight="300px">
        <BlogPosts />
      </LazyComponent>
      <LocationGeoList geoList={geoList} locationData={locationData} />
    </>
  );
};

export default LocationSkuPage;
